//-------------------------------------------------------------------------------------------------- Imports
import { Button, Card, Select, Switch, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { StyleSheet } from "../../StyleSheet";
import SubDefaultLayout from "../../components/SubDefaultLayout";
import InfoCardComponent from "../../components/InfoCardComponent";
import Loading from "../../components/Loading";
import { AppDispatch } from "../../stores/store";
import { clearAlert, setAlert, showAlert } from "../../stores/slices/alertSlice";
import {
    getInternetSeguroCategories,
    getInternetSeguroUserPolicies,
    scheduleInternetSeguroRule,
    toggleInternetSeguroRule,
} from "../../stores/slices/internetSeguroSlice";
import {
    InternetSeguroCategory,
    InternetSeguroPolicy,
    InternetSeguroSchedule,
} from "../../types/common";
import {
    DAY_CODES,
    DAY_CODE_TO_DISPLAY_NAME,
    DayTimeRange,
    TIME_SLOTS,
    dayRangesToSchedule,
    hasCompleteRange,
    scheduleToDayRanges,
} from "./scheduleHelpers";

const { Text } = Typography;

//-------------------------------------------------------------------------------------------------- View
const ParentalControlEditRule = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { userId, categoryCode } = useParams();

    const categories = useSelector(
        (state: any) => state.internetSeguro.categories as InternetSeguroCategory[] | null
    );
    const categoriesLoaded = useSelector((state: any) => state.internetSeguro.categoriesLoaded);
    const category: InternetSeguroCategory | undefined = (categories ?? []).find(
        (c) => c.categoryCode === categoryCode
    );
    const userPolicies = useSelector(
        (state: any) =>
            state.internetSeguro.userPolicies[userId as string] as
                | Record<string, InternetSeguroPolicy>
                | undefined
    );
    const userPoliciesLoaded = useSelector(
        (state: any) => state.internetSeguro.userPoliciesLoaded[userId as string]
    );
    const policy: InternetSeguroPolicy | undefined = userPolicies?.[categoryCode as string];

    useEffect(() => {
        if (!categoriesLoaded) {
            dispatch(getInternetSeguroCategories());
        }
    }, [dispatch, categoriesLoaded]);

    useEffect(() => {
        if (userId && !userPoliciesLoaded) {
            dispatch(getInternetSeguroUserPolicies(userId));
        }
    }, [dispatch, userId, userPoliciesLoaded]);

    const [rulesEnabled, setRulesEnabled] = useState(policy?.enable ?? false);
    const [scheduleEnabled, setScheduleEnabled] = useState(() =>
        Object.entries(policy?.schedule ?? {}).some(
            ([day, value]) => day !== "time_zone" && !!value
        )
    );
    const [daySchedules, setDaySchedules] = useState<Record<string, DayTimeRange>>(() =>
        scheduleToDayRanges(policy?.schedule)
    );
    const [selectedDay, setSelectedDay] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setRulesEnabled(policy?.enable ?? false);
        setScheduleEnabled(
            Object.entries(policy?.schedule ?? {}).some(
                ([day, value]) => day !== "time_zone" && !!value
            )
        );
        setDaySchedules(scheduleToDayRanges(policy?.schedule));
    }, [policy]);

    const selectedDayRange = selectedDay ? daySchedules[selectedDay] : undefined;
    const assignedDays = useMemo(
        () => Object.keys(daySchedules).filter((dayCode) => hasCompleteRange(daySchedules[dayCode])),
        [daySchedules]
    );

    const openAlert = (type: "SUCCESS" | "ERROR", title: string, message: string, navigateBack: boolean) => {
        dispatch(setAlert({
            show: true,
            type,
            title,
            message,
            onFinalize: () => {
                dispatch(showAlert(false));
                dispatch(clearAlert());
                if (navigateBack) {
                    navigate(`/parental-control/users/${userId}/rules`);
                }
            },
        }));
    };

    const handleSave = async () => {
        if (!category) {
            return;
        }
        setSaving(true);
        let hadError = false;
        let errorMessage = "Hubo un error al guardar la regla. Intente más tarde.";

        if (rulesEnabled !== (policy?.enable ?? false)) {
            const result = await dispatch(
                toggleInternetSeguroRule({
                    userId: userId as string,
                    categoryCode: category.categoryCode,
                    enabled: rulesEnabled,
                })
            );
            if (toggleInternetSeguroRule.rejected.match(result)) {
                hadError = true;
                errorMessage = (result.error as any)?.message || errorMessage;
            }
        }

        if (!hadError && category.scheduled) {
            if (scheduleEnabled) {
                const schedule: InternetSeguroSchedule = dayRangesToSchedule(daySchedules);
                if (Object.keys(schedule).length > 1) {
                    const result = await dispatch(
                        scheduleInternetSeguroRule({
                            userId: userId as string,
                            categoryCode: category.categoryCode,
                            schedule,
                        })
                    );
                    if (scheduleInternetSeguroRule.rejected.match(result)) {
                        hadError = true;
                        errorMessage = (result.error as any)?.message || errorMessage;
                    }
                } else if (policy?.schedule) {
                    const result = await dispatch(
                        scheduleInternetSeguroRule({
                            userId: userId as string,
                            categoryCode: category.categoryCode,
                            schedule: { time_zone: "America/Mexico_City" },
                        })
                    );
                    if (scheduleInternetSeguroRule.rejected.match(result)) {
                        hadError = true;
                        errorMessage = (result.error as any)?.message || errorMessage;
                    }
                }
            } else if (policy?.schedule) {
                const result = await dispatch(
                    scheduleInternetSeguroRule({
                        userId: userId as string,
                        categoryCode: category.categoryCode,
                        schedule: { time_zone: "America/Mexico_City" },
                    })
                );
                if (scheduleInternetSeguroRule.rejected.match(result)) {
                    hadError = true;
                    errorMessage = (result.error as any)?.message || errorMessage;
                }
            }
        }

        setSaving(false);
        openAlert(
            hadError ? "ERROR" : "SUCCESS",
            "Reglas de usuario",
            hadError ? errorMessage : "La regla se guardó correctamente.",
            !hadError
        );
    };

    const handleClearDaySchedule = () => {
        if (!selectedDay) {
            return;
        }
        setDaySchedules((prev) => {
            const next = { ...prev };
            delete next[selectedDay];
            return next;
        });
        setSelectedDay(null);
    };

    const handleTimeChange = (key: "start" | "end", value: string) => {
        if (!selectedDay) {
            return;
        }
        setDaySchedules((prev) => {
            const next = { ...prev };
            const current = next[selectedDay] ?? { start: "", end: "" };
            next[selectedDay] = { ...current, [key]: value };
            return next;
        });
    };

    if (!category) {
        return <Loading />;
    }

    const timeSelectOptions = TIME_SLOTS.map((slot) => ({ value: slot, label: slot }));

    return (
        <SubDefaultLayout
            title={category.displayName}
            needReturn={true}
            navigateTo={`/parental-control/users/${userId}/rules`}
        >
            <div style={styles.contentContainer}>
                <div style={styles.switchRow}>
                    <Text style={styles.switchLabel}>Habilitar reglas</Text>
                    <Switch
                        checked={rulesEnabled}
                        onChange={(value) => setRulesEnabled(value)}
                    />
                </div>
                {category.scheduled && (
                    <div style={styles.switchRow}>
                        <Text style={styles.switchLabel}>Habilitar horarios</Text>
                        <Switch
                            checked={scheduleEnabled}
                            onChange={(value) => setScheduleEnabled(value)}
                            disabled={!rulesEnabled}
                        />
                    </div>
                )}
                {category.scheduled && scheduleEnabled && rulesEnabled && (
                    <div style={styles.scheduleSection}>
                        <InfoCardComponent
                            color="#c5d5e4"
                            title="Horarios"
                            description="Define los horarios en los que los dispositivos podrán acceder a este contenido."
                        />
                        <Text style={styles.sectionTitle}>Días de la semana</Text>
                        <div style={styles.daysContainer}>
                            {DAY_CODES.map((dayCode) => {
                                const isAssigned = assignedDays.includes(dayCode);
                                const isSelected = selectedDay === dayCode;
                                return (
                                    <Button
                                        key={dayCode}
                                        style={{
                                            ...styles.dayButton,
                                            backgroundColor: isSelected
                                                ? "#0055c2"
                                                : isAssigned
                                                    ? "#e6f0ff"
                                                    : "#f2f2f2",
                                            color: isSelected ? "white" : "#0055c2",
                                            borderColor: isAssigned ? "#0055c2" : "#d9d9d9",
                                        }}
                                        onClick={() => setSelectedDay(dayCode)}
                                    >
                                        {DAY_CODE_TO_DISPLAY_NAME[dayCode]}
                                    </Button>
                                );
                            })}
                        </div>
                        {selectedDay && (
                            <div style={styles.selectedDaySection}>
                                <Text style={styles.sectionTitle}>
                                    Define horario del{" "}
                                    {DAY_CODE_TO_DISPLAY_NAME[selectedDay as keyof typeof DAY_CODE_TO_DISPLAY_NAME]}
                                </Text>
                                <Card style={styles.cardStyles}>
                                    <div style={styles.timeSelectsRow}>
                                        <Select
                                            placeholder="Hora inicio"
                                            value={selectedDayRange?.start || undefined}
                                            options={timeSelectOptions}
                                            onChange={(value) => handleTimeChange("start", value)}
                                            style={{ width: "100%" }}
                                        />
                                        <Text style={styles.timeSeparator}>a</Text>
                                        <Select
                                            placeholder="Hora fin"
                                            value={selectedDayRange?.end || undefined}
                                            options={timeSelectOptions}
                                            onChange={(value) => handleTimeChange("end", value)}
                                            style={{ width: "100%" }}
                                        />
                                    </div>
                                </Card>
                                {hasCompleteRange(selectedDayRange) && (
                                    <div style={styles.deleteButtonContainer}>
                                        <Button
                                            style={styles.buttonOrange}
                                            onClick={handleClearDaySchedule}
                                        >
                                            Eliminar horario del día
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                <div style={styles.buttonContainer}>
                    <Button
                        size="large"
                        style={styles.buttonPrimary}
                        onClick={handleSave}
                        loading={saving}
                    >
                        Guardar reglas
                    </Button>
                    <Button
                        size="large"
                        style={styles.buttonSecondary}
                        onClick={() => navigate(`/parental-control/users/${userId}/rules`)}
                    >
                        Cancelar
                    </Button>
                </div>
            </div>
        </SubDefaultLayout>
    );
};

//-------------------------------------------------------------------------------------------------- Styles
const styles = StyleSheet.create({
    contentContainer: {
        width: "70%",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
    },
    switchRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 15,
        marginBottom: 15,
        width: "70%",
        alignSelf: "center",
    },
    switchLabel: {
        fontSize: 15,
    },
    scheduleSection: {
        marginTop: 10,
    },
    sectionTitle: {
        display: "block",
        textAlign: "center",
        fontSize: 16,
        fontWeight: 600,
        color: "#0055c1",
        margin: "15px 0",
    },
    daysContainer: {
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: 10,
        marginBottom: 10,
    },
    dayButton: {
        borderRadius: 20,
        fontWeight: 600,
    },
    selectedDaySection: {
        marginTop: 10,
    },
    cardStyles: {
        boxShadow: "0px 0px 8px #00000029",
        borderRadius: 15,
        width: "60%",
        margin: "0 auto",
    },
    timeSelectsRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
    },
    timeSeparator: {
        fontSize: 16,
        fontWeight: 600,
    },
    deleteButtonContainer: {
        display: "flex",
        justifyContent: "center",
        marginTop: 15,
    },
    buttonOrange: {
        backgroundColor: "#FF7A00",
        borderRadius: 30,
        color: "white",
        border: "none",
        padding: "5px 30px",
    },
    buttonContainer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        gap: 15,
        marginTop: 25,
        marginBottom: 30,
    },
    buttonPrimary: {
        backgroundColor: "#0055c2",
        borderRadius: 30,
        color: "white",
        border: "none",
        padding: "5px 30px",
    },
    buttonSecondary: {
        borderRadius: 30,
        color: "#0055c2",
        borderColor: "#0055c2",
        padding: "5px 30px",
    },
});

//-------------------------------------------------------------------------------------------------- Exports
export default ParentalControlEditRule;